import React, { CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { ROUTES } from '../../routes/paths';
import { Features } from '../../../assets/features/Features';
import { useLanguage } from '../../../context/LanguageContext';
import { useTheme } from '../../../context/ThemeContext';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

import pilates1 from '../../../assets/img/gallery/pilates/1.jpeg';
import pilates2 from '../../../assets/img/gallery/pilates/2.jpeg';
import pilates3 from '../../../assets/img/gallery/pilates/3.jpeg';
import pilates4 from '../../../assets/img/gallery/pilates/4.jpeg';
import pilates5 from '../../../assets/img/gallery/pilates/5.jpeg';
import pilates6 from '../../../assets/img/gallery/pilates/6.jpeg';
import pilates7 from '../../../assets/img/gallery/pilates/7.jpeg';
import pilates8 from '../../../assets/img/gallery/pilates/8.jpeg';
import pilates9 from '../../../assets/img/gallery/pilates/9.jpeg';

const pilatesImages = [pilates1, pilates2, pilates3, pilates4, pilates5, pilates6, pilates7, pilates8, pilates9];

const ClinicalPilates: React.FC = () => {
  const { language } = useLanguage();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';

  const cardStyle: CSSProperties = {
    backgroundColor: isDarkMode ? '#2a2c2e' : '#f8f9fa',
    border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
  };

  return (
    <section id="clinical-pilates" className="clinical-pilates section">
      <div className="container" data-aos="fade-up">
        <div className="row align-items-center">
          {/* Text Content - Left */}
          <div className="col-lg-6 mb-4 mb-lg-0">
            <div className="p-4 p-lg-5 rounded-4" style={cardStyle}>
              <span className="badge bg-primary mb-3 px-3 py-2">
                <i className="bi bi-activity me-1"></i>
                {Features.clinicalPilates.title[language]}
              </span>
              <h2 className="fw-bold mb-3" style={isDarkMode ? { color: '#ffffff' } : {}}>
                {Features.clinicalPilates.subtitle[language]}
              </h2>
              <p className="lead mb-4" style={isDarkMode ? { color: '#d1d5db' } : {}}>
                {Features.clinicalPilates.description[language]}
              </p>
              <div className="row g-3 mb-4">
                <div className="col-6">
                  <div className="d-flex align-items-center">
                    <i className="bi bi-shield-check text-primary fs-4 me-2"></i>
                    <small className="fw-semibold" style={isDarkMode ? { color: '#f3f4f6' } : {}}>
                      {language === 'en' ? 'Pain Stabilization' : 'Estabilização da Dor'}
                    </small>
                  </div>
                </div>
                <div className="col-6">
                  <div className="d-flex align-items-center">
                    <i className="bi bi-body-text text-primary fs-4 me-2"></i>
                    <small className="fw-semibold" style={isDarkMode ? { color: '#f3f4f6' } : {}}>
                      {language === 'en' ? 'Flexibility' : 'Flexibilidade'}
                    </small>
                  </div>
                </div>
                <div className="col-6">
                  <div className="d-flex align-items-center">
                    <i className="bi bi-arrow-repeat text-primary fs-4 me-2"></i>
                    <small className="fw-semibold" style={isDarkMode ? { color: '#f3f4f6' } : {}}>
                      {language === 'en' ? 'Prevention' : 'Prevenção'}
                    </small>
                  </div>
                </div>
                <div className="col-6">
                  <div className="d-flex align-items-center">
                    <i className="bi bi-heart-pulse text-primary fs-4 me-2"></i>
                    <small className="fw-semibold" style={isDarkMode ? { color: '#f3f4f6' } : {}}>
                      {language === 'en' ? 'Spine Strength' : 'Força da Coluna'}
                    </small>
                  </div>
                </div>
              </div>
              <Link to={ROUTES.CLINICAL_PILATES} className="btn btn-primary rounded-pill px-4">
                {language === 'en' ? 'Learn More' : 'Saiba Mais'}
              </Link>
            </div>
          </div>

          {/* Image Slider - Right */}
          <div className="col-lg-6" data-aos="fade-left">
            <div className="pilates-slider-wrapper rounded-4 overflow-hidden shadow">
              <Swiper
                modules={[Navigation, Pagination, Autoplay]}
                navigation
                pagination={{ clickable: true }}
                autoplay={{ delay: 3500, disableOnInteraction: false }}
                loop={true}
                spaceBetween={0}
                slidesPerView={1}
                className="pilates-swiper"
                style={{ borderRadius: '1rem' }}
              >
                {pilatesImages.map((img, index) => (
                  <SwiperSlide key={index}>
                    <img
                      src={img}
                      alt={`Pilates studio ${index + 1}`}
                      style={{
                        width: '100%',
                        height: '480px',
                        objectFit: 'cover',
                        display: 'block',
                      }}
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .pilates-swiper {
          border-radius: 1rem;
        }

        .pilates-swiper .swiper-button-next,
        .pilates-swiper .swiper-button-prev {
          color: #ffffff;
          background: rgba(0, 0, 0, 0.35);
          width: 40px;
          height: 40px;
          border-radius: 50%;
          transition: background 0.3s ease;
        }

        .pilates-swiper .swiper-button-next:hover,
        .pilates-swiper .swiper-button-prev:hover {
          background: rgba(25, 119, 204, 0.8);
        }

        .pilates-swiper .swiper-button-next:after,
        .pilates-swiper .swiper-button-prev:after {
          font-size: 16px;
          font-weight: bold;
        }

        .pilates-swiper .swiper-pagination-bullet {
          background: #ffffff;
          opacity: 0.6;
          width: 10px;
          height: 10px;
        }

        .pilates-swiper .swiper-pagination-bullet-active {
          opacity: 1;
          background: var(--primary-color, #1977cc);
        }

        @media (max-width: 991.98px) {
          .pilates-swiper img {
            height: 350px !important;
          }
        }

        @media (max-width: 575.98px) {
          .pilates-swiper img {
            height: 260px !important;
          }

          .pilates-swiper .swiper-button-next,
          .pilates-swiper .swiper-button-prev {
            width: 32px;
            height: 32px;
          }

          .pilates-swiper .swiper-button-next:after,
          .pilates-swiper .swiper-button-prev:after {
            font-size: 12px;
          }
        }
      `}</style>
    </section>
  );
};

export default ClinicalPilates;
